import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { EntityWithLabel } from '../../api/ApiTypes';
import { Button, Input, usePrompt } from '../../ui';
import { ResultInfoForMutation } from '../../ui/ResultInfo';
import { AppEvents } from '../AppEvents';
import { useResolve } from '../Container';
import { Copy, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';

const useBoardRenameMutation = () => {
    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);
    return useMutation({
        mutationFn(params: { boardId: string, value: string }) {
            return api.exec(v2.boards.patch(params.boardId, { name: params.value }));
        },
        onSuccess(data, params) {
            evts.push('board:patch', { board: { id: params.boardId, label: params.value } });
        }
    });
};

export const newBoardRenamePopup = (board: EntityWithLabel) => ({ ui: NewBoardRenamePopup, args: { board } });

export const NewBoardRenamePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { board } = args;

    const mutation = useBoardRenameMutation();

    const [value, setValue] = useState(board.label);

    const invalid = !value.trim();

    const onAction = (confirm: boolean) => {
        if (confirm) {
            if (!mutation.isLoading) {
                mutation.mutateAsync({
                    boardId: board.id,
                    value
                }).then(() => action(true));
            }
        }
        else {
            dismiss();
        }
    };

    const promptRef = usePrompt({
        onAction: mutation.isLoading ? null : onAction
    });

    return <Flexer ref={promptRef}>
        <Popup w={32}>
            <Popup.Close onAction={() => onAction(false)} />
            <Popup.Title>Rename Board</Popup.Title>
            <Popup.Content>
                <Copy>
                    Enter a new board name:
                </Copy>
                <Input
                    placeholder=""
                    value={value}
                    maxLength={64}
                    autoFocus
                    onChange={setValue}
                />
                <Spacer f={1} />
                <ResultInfoForMutation mutation={mutation} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={invalid || mutation.isLoading} variant="primary" onAction={() => onAction(true)}>
                    {mutation.isLoading ? 'Applying...' : 'Apply'}
                </Button>
                <Button disabled={mutation.isLoading} onAction={() => onAction(false)}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Flexer>;
};
